<template>
  <div class="phone-number-list">
    <max-phone-number-warning :max-select-phone-numbers="maxSelectPhoneNumbers" v-if="disabledCheckBoxList" />
    <div
      v-loading="loading"
      class="phone-number-list__content"
      :class="{'phone-number-list__content--empty': emptyResult}">
      <p v-if="emptyResult">{{ emptyResultTips || $t('billing.pbx.no_results') }}</p>
      <zm-checkbox-list
        v-if="!emptyResult"
        v-model="checkedNumberList"
        :items="currentList"
        :column-count="2"
        :span="12"
        :disabled="disabledCheckBoxList"
      />
    </div>
    <zm-button
      v-if="refreshBtnVisible"
      data-testid="checkout-button-194"
      :disabled="disabledRefreshBtn"
      class="phone-number-list__refresh"
      icon="zm-icon-refresh"
      type="link"
      @click="onRefresh"
    >
      {{ $t('billing.pbx.refresh') }}
    </zm-button>
  </div>
</template>

<script>
import { isEqual, reduce, slice } from 'lodash';
import MaxPhoneNumberWarning from '@/plans/ZOOM_PHONE/component/max-phone-number-warning';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { maxSelectPhoneNumbers, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import { logger } from '@/utils/log';
import { getCurrentTrackingPlanName } from '@/plans/ZOOM_PHONE/helper/utils';

const defaultPageCount = 10;

export default {
  name: 'PhoneNumberFormList',
  components: { MaxPhoneNumberWarning },
  mixins: [computedMixin],
  props: {
    loading: Boolean,
    type: {
      type: String,
      default: phoneNumberTypeMap.local,
    },
    phoneNumberList: {
      type: Array,
      default: () => [],
    },
    disabledRefresh: {
      type: Boolean,
      default: false,
    },
    checkedPhoneNumberMap: {
      type: Object,
      default: () => {
      },
    },
    localNumberShowClear: {
      type: Boolean,
    },
    portNumberCount: {
      type: Number,
      default: 0,
    },
    maxSelectPhoneNumbers: {
      type: Number,
      default: maxSelectPhoneNumbers,
    },
    emptyResultTips: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      checkedNumberList: [],
      currentList: [],
      startCount: 0,
    };
  },
  computed: {
    trackingPlanName() {
      return getCurrentTrackingPlanName();
    },
    phoneNumberMap() {
      return reduce(this.phoneNumberList, (pre, item) => {
        return { ...pre, [item.label]: item };
      }, {});
    },
    checkedPhoneNumberMapKeys() {
      return Object.keys(this.checkedPhoneNumberMap);
    },
    disabledCheckBoxList() {
      return (this.checkedPhoneNumberMapKeys?.length + this.portNumberCount) >= this.maxSelectPhoneNumbers;
    },
    disabledRefreshBtn() {
      return this.disabledRefresh || this.loading;
    },
    emptyResult() {
      return this.currentList?.length === 0;
    },
    refreshBtnVisible() {
      return this.phoneNumberList?.length > 0;
    }
  },
  methods: {
    changeCurrentList(list = []) {
      this.currentList = this.mapDisabledPhoneNumberList(list);
    },
    gotoNextPage() {
      let prevStartCount = this.startCount; // 100

      if (this.startCount >= this.phoneNumberList.length) {
        prevStartCount = 0;
        this.startCount = prevStartCount + defaultPageCount;
      } else {
        this.startCount = this.startCount + defaultPageCount;
      }

      const currentList = slice(this.phoneNumberList, prevStartCount, this.startCount);

      this.changeCurrentList(currentList);
    },
    mapDisabledPhoneNumberList(list) {
      if (this.type === phoneNumberTypeMap.local) {
        return list.map((item) => {
          const isCurrent = !!this.checkedPhoneNumberMap[item.phoneNumber];
          const disabled = this.localNumberShowClear ? false : isCurrent;

          return {
            ...item,
            disabled,
          };
        });
      }

      return list;
    },
    onRefresh() {
      this.gotoNextPage();
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - pick your numbers - ${this.type} - refresh results`);
    },
    checkExistUnreadPhoneNumbers() {
      return this.phoneNumberList?.length > 0;
    },
  },
  watch: {
    phoneNumberList: {
      deep: true,
      immediate: true,
      handler() {
        this.startCount = 0;
        this.gotoNextPage();
      },
    },
    checkedNumberList: {
      handler(list) {
        // Find the dto with the current number complete according to the number id currently selected by the user
        const checkedMap = reduce(list, (pre, p) => {
          // search by current phone number list or previously selected number list
          const item = this.phoneNumberMap[p] || this.checkedPhoneNumberMap[p];
          return {
            ...pre,
            ...item ? { [p]: item } : {},
          };
        }, {});

        this.$emit('checkedNumberList', checkedMap);
      },
    },
    checkedPhoneNumberMapKeys: {
      immediate: true,
      handler(val) {
        // When a customer deletes a previously selected number, should trigger checklist change
        if (!isEqual(val, this.checkedNumberList)) {
          this.checkedNumberList = val;
        }
      },
    },
    localNumberShowClear: {
      immediate: true,
      handler(val) {
        logger.log('localNumberShowClear', val);

        if (this.type === phoneNumberTypeMap.local) {
          this.changeCurrentList(this.currentList);
        }
      },
    },
    emptyResult: {
      handler(emptyResult) {
        if (emptyResult) {
          this.$tracking.buyInteraction(
            `${this.trackingPlanName} numbers`,
            `${this.callingPlanName} - pick your numbers - ${this.type} - error - no results found please try again`);
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.phone-number-list {
  margin-top: 8px;
  text-align: center;

  .zm-button--link {
    color: #0B5CFF;
  }

  .max-phone-number {
    &__alert {
      margin-bottom: 8px;
    }
  }

  &__content {
    width: 100%;
    padding: 16px 15px;
    min-height: 210px;
    border: 1px solid #B8B8C4;
    border-radius: 10px;
    margin: auto;

    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .zm-checkbox-list {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .zm-col {
        text-align: left;
      }

      .zm-checkbox__label {
        @include media(mobile) {
          &-inner {
            font-size: 14px;
          }
        }

        &.is-checked {
          .zm-checkbox__inner {
            border-color: #0B5CFF;
            background-color: #0B5CFF;
          }

          &.is-disabled {
            .zm-checkbox__inner {
              border-color: #b9d0ed;
              background-color: #b9d0ed;
            }
          }
        }
      }
    }

    .zm-loading-mask {
      border-radius: 15px;

      .zm-loading-spinner {
        svg {
          width: 24px;
        }
      }
    }
  }

  &__refresh {
    text-transform: capitalize;
  }
}
</style>
